import { useState, useLayoutEffect, useEffect, useCallback } from 'react'
import useFrameCoalescedCallback from './useFrameCoalescedCallback'
import useVisibleSlice from './useVisibleSlice'
import { hostWindow } from '../../shared/host_window'
import { getVisibleSlice } from '../utils/visible_slice'
import { sheetFromVisibleSlice } from '../utils/viewport_utils'

// Share of the visible viewport the sheet may occupy, matching the 88dvh the
// stylesheet falls back to.
const SHEET_VIEWPORT_RATIO = 0.88

// Where the sheet has to sit for all of it to be on screen.
//
// The sheet is `position: fixed; bottom: 0`, and both `bottom` and `dvh` are
// resolved against the layout viewport, which does not shrink when the
// on-screen keyboard opens. Sizing alone therefore cannot help: a shorter
// sheet still ends at the bottom of the layout viewport, so it only pulls its
// own top edge down behind the keyboard. The bottom edge has to come up by the
// keyboard's height as well, which is what visualViewport measures.
//
// iOS keeps the layout viewport and slides the visual one; Android usually
// resizes the layout viewport outright, leaving an inset of zero. Both end up
// with the sheet inside the visible area.
const measureViewport = () => {
  // A foreign host (utils/visible_slice): the frame's visible slice is the
  // viewport the sheet has to fill.
  const slice = getVisibleSlice()
  if (slice) {
    return sheetFromVisibleSlice(slice, window.innerHeight, SHEET_VIEWPORT_RATIO)
  }

  const view = hostWindow()
  const visual = view.visualViewport

  // A pinched or auto-zoomed page reports the visual viewport in a different
  // scale from the layout viewport `bottom` resolves against, so the two
  // cannot be mixed. Leave the sheet on the stylesheet's dvh sizing there.
  if (!visual || (visual.scale && Math.abs(visual.scale - 1) > 0.01)) {
    return { height: Math.round(view.innerHeight * SHEET_VIEWPORT_RATIO), bottom: 0 }
  }

  const inset = Math.max(0, Math.round(view.innerHeight - visual.height - visual.offsetTop))

  return { height: Math.round(visual.height * SHEET_VIEWPORT_RATIO), bottom: inset }
}

// The sheet's { height, bottom } in pixels, recomputed while active is true.
const useSheetViewport = (active) => {
  const [ viewport, setViewport ] = useState(null)

  const update = useCallback(() => {
    const next = measureViewport()
    // Keep the previous object when nothing moved so React bails out of the
    // re-render - visualViewport scroll fires far more often than the sheet
    // actually needs to move.
    setViewport((prev) =>
      (prev && prev.height === next.height && prev.bottom === next.bottom ? prev : next))
  }, [])

  // The keyboard animating in fires a burst of resize and scroll events.
  const scheduleUpdate = useFrameCoalescedCallback(update)

  // Pre-paint, so the sheet opens measured instead of flashing at the
  // stylesheet's dvh fallback first.
  useLayoutEffect(() => {
    if (active) {
      update()
    }
  }, [ active, update ])

  useEffect(() => {
    if (!active) {
      return undefined
    }

    const view = hostWindow()
    const visual = view.visualViewport

    view.addEventListener('resize', scheduleUpdate)
    view.addEventListener('orientationchange', scheduleUpdate)
    if (visual) {
      visual.addEventListener('resize', scheduleUpdate)
      // iOS offsets the visual viewport without resizing it, so the inset
      // changes on scroll as well as on resize.
      visual.addEventListener('scroll', scheduleUpdate, { passive: true })
    }

    return () => {
      view.removeEventListener('resize', scheduleUpdate)
      view.removeEventListener('orientationchange', scheduleUpdate)
      if (visual) {
        visual.removeEventListener('resize', scheduleUpdate)
        visual.removeEventListener('scroll', scheduleUpdate)
      }
    }
  }, [ active, scheduleUpdate ])

  // A foreign host's scroll reaches none of the listeners above.
  useVisibleSlice(active, scheduleUpdate)

  return viewport
}

export default useSheetViewport
